/**
 * LP Tracker — rastreamento de comportamento na landing page.
 *
 * Responsabilidades:
 *  - Gerar/persistir UUID de sessão (sessionStorage)
 *  - Criar sessão no backend (uma vez por visita)
 *  - Enviar eventos (pageview, click, quiz, scroll, lead)
 *  - Heartbeat a cada 30s para atualizar tempo na página
 *
 * Design: fire-and-forget — erros são silenciados para nunca impactar a UX.
 */

const API_BASE = document.URL.indexOf('localhost') !== -1
  ? 'http://localhost:8000/api'
  : 'https://webservice.zapermenu.com.br/api';
const SESSION_KEY = 'lp_session_uuid';
const HEARTBEAT_INTERVAL_MS = 30_000;

let _sessaoUuid = null;
let _rotaAtual = '/';
let _rotaEntradaEm = Date.now();
let _sessaoIniciouEm = Date.now();
let _heartbeatTimer = null;
let _initialized = false;

// ── Utilidades ────────────────────────────────────────────────────────────────

function gerarUuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function obterOuCriarUuid() {
  let uuid = sessionStorage.getItem(SESSION_KEY);
  if (!uuid) {
    uuid = gerarUuid();
    sessionStorage.setItem(SESSION_KEY, uuid);
  }
  return uuid;
}

function post(endpoint, payload) {
  // sendBeacon envia com credentials:'include' por padrão, causando CORS com Allow-Origin:*.
  // Usamos fetch com keepalive:true (mesmo comportamento de não bloquear o unload)
  // e credentials:'omit' para compatibilidade com qualquer configuração de CORS.
  fetch(`${API_BASE}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {});
}

function tempoNaRota() {
  return Math.round((Date.now() - _rotaEntradaEm) / 1000);
}

function tempoTotal() {
  return Math.round((Date.now() - _sessaoIniciouEm) / 1000);
}

// ── Heartbeat ─────────────────────────────────────────────────────────────────

function iniciarHeartbeat() {
  pararHeartbeat();
  _heartbeatTimer = setInterval(() => {
    post('lp-tracking/heartbeat', {
      sessao_uuid:   _sessaoUuid,
      tempo_segundos: tempoTotal(),
      rota_atual:    _rotaAtual,
    });
  }, HEARTBEAT_INTERVAL_MS);
}

function pararHeartbeat() {
  if (_heartbeatTimer) {
    clearInterval(_heartbeatTimer);
    _heartbeatTimer = null;
  }
}

// ── API pública ───────────────────────────────────────────────────────────────

/**
 * Inicializa o tracker. Deve ser chamado uma única vez no App.vue, após
 * os parâmetros de URL já estarem salvos no store (urlParams).
 *
 * @param {Object} urlParams   - { ad, utm, ref, pxl } do Vuex store
 * @param {string} primeirPath - rota inicial (ex: '/')
 */
export function inicializar(urlParams = {}, primeiroPath = '/') {
  if (_initialized) return;
  _initialized = true;

  _sessaoUuid = obterOuCriarUuid();
  _rotaAtual = primeiroPath;
  _rotaEntradaEm = Date.now();
  _sessaoIniciouEm = Date.now();

  post('lp-tracking/sessao', {
    uuid:          _sessaoUuid,
    primeiro_path: primeiroPath,
    anuncio_uuid:  urlParams.ad  || null,
    utm:           urlParams.origem || urlParams.utm || null,
    referral_id:   urlParams.ref || null,
    pixel_id:      urlParams.pxl || null,

    // De onde veio a atribuição desta sessão: 'url' (clique de verdade),
    // 'storage' (resgatada de uma visita anterior), 'referrer' ou 'direto'.
    // Sem isso, resgate vira clique no dashboard e o anúncio melhora sozinho.
    atribuicao_fonte: urlParams.fonte || null,

    // Id do navegador (localStorage, 90 dias). É o que costura as visitas do
    // mesmo visitante em histórico na ficha do lead — o `uuid` acima vive em
    // sessionStorage e nasce de novo a cada aba.
    visitante_uuid: urlParams.visitante_uuid || null,
  });

  iniciarHeartbeat();

  // Ao sair da página: registra tempo na rota atual e atualiza o heartbeat
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      post('lp-tracking/evento', {
        sessao_uuid:            _sessaoUuid,
        tipo:                   'pageview',
        rota:                   _rotaAtual,
        dados:                  { nova_rota: null, exit: true },
        tempo_na_rota_segundos: tempoNaRota(),
      });
      post('lp-tracking/heartbeat', {
        sessao_uuid:    _sessaoUuid,
        tempo_segundos: tempoTotal(),
        rota_atual:     _rotaAtual,
      });
    }
  });
}

/**
 * Registra troca de rota. Chamado no router.afterEach().
 *
 * O evento registra o tempo na rota ANTERIOR (_rotaAtual), pois só sabemos
 * quanto tempo o usuário ficou numa rota quando ele a abandona.
 * O campo dados.nova_rota informa para qual rota o usuário foi, e é usado
 * pelo backend para atualizar ultima_rota da sessão.
 */
export function trackPageview(novaRota) {
  if (!_sessaoUuid) {
    // Ainda não inicializado — apenas atualiza a rota e o timer
    _rotaAtual = novaRota;
    _rotaEntradaEm = Date.now();
    return;
  }

  const tempoAnterior = tempoNaRota();

  post('lp-tracking/evento', {
    sessao_uuid:            _sessaoUuid,
    tipo:                   'pageview',
    rota:                   _rotaAtual,
    dados:                  { nova_rota: novaRota },
    tempo_na_rota_segundos: tempoAnterior,
  });

  _rotaAtual = novaRota;
  _rotaEntradaEm = Date.now();
}

/**
 * Registra clique em um elemento.
 * @param {string} label  - texto descritivo do botão/link
 * @param {Object} dados  - metadados extras (opcional)
 */
export function trackClick(label, dados = {}) {
  post('lp-tracking/evento', {
    sessao_uuid:            _sessaoUuid,
    tipo:                   'click',
    rota:                   _rotaAtual,
    label,
    dados,
    tempo_na_rota_segundos: tempoNaRota(),
  });
}

/**
 * Registra início do quiz.
 * @param {string} quiz - identificador do quiz ('checkout' | 'whatsapp')
 */
export function trackQuizInicio(quiz) {
  post('lp-tracking/evento', {
    sessao_uuid:            _sessaoUuid,
    tipo:                   'quiz_inicio',
    rota:                   _rotaAtual,
    label:                  quiz,
    tempo_na_rota_segundos: tempoNaRota(),
  });
}

/**
 * Registra resposta de pergunta do quiz.
 * @param {string} quiz      - 'checkout' | 'whatsapp'
 * @param {number} step      - número da pergunta (1-based)
 * @param {string} pergunta  - label da pergunta
 * @param {*}      resposta  - resposta dada
 */
export function trackQuizResposta(quiz, step, pergunta, resposta) {
  post('lp-tracking/evento', {
    sessao_uuid:            _sessaoUuid,
    tipo:                   'quiz_resposta',
    rota:                   _rotaAtual,
    label:                  `${quiz} - Step ${step}: ${pergunta}`,
    dados:                  { quiz, step, pergunta, resposta: String(resposta).slice(0, 200) },
    tempo_na_rota_segundos: tempoNaRota(),
  });
}

/**
 * Registra conclusão do quiz (lead enviado).
 * @param {string} quiz        - 'checkout' | 'whatsapp'
 * @param {string} contatoUuid - UUID retornado pela API
 */
export function trackQuizCompleto(quiz, contatoUuid = null) {
  post('lp-tracking/evento', {
    sessao_uuid:            _sessaoUuid,
    tipo:                   'quiz_completo',
    rota:                   _rotaAtual,
    label:                  quiz,
    dados:                  { quiz, contato_uuid: contatoUuid },
    tempo_na_rota_segundos: tempoNaRota(),
  });
}

/**
 * Registra conversão (lead criado). Vincula sessão ao contato.
 * @param {string} contatoUuid - UUID retornado pela API
 */
export function trackLeadCriado(contatoUuid) {
  post('lp-tracking/evento', {
    sessao_uuid:            _sessaoUuid,
    tipo:                   'lead_criado',
    rota:                   _rotaAtual,
    label:                  'lead_criado',
    dados:                  { contato_uuid: contatoUuid },
    tempo_na_rota_segundos: tempoNaRota(),
  });
}

/**
 * Registra profundidade de scroll atingida.
 * @param {number} profundidade - percentual (25, 50, 75, 100)
 */
export function trackScrollDepth(profundidade) {
  post('lp-tracking/evento', {
    sessao_uuid:            _sessaoUuid,
    tipo:                   'scroll_depth',
    rota:                   _rotaAtual,
    label:                  `${profundidade}%`,
    dados:                  { profundidade },
    tempo_na_rota_segundos: tempoNaRota(),
  });
}

export function getSessaoUuid() {
  return _sessaoUuid;
}
